import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiPermissions, IntegrationApp, PbxIntegrationApp, PbxLabel, PbxLabel_Type, PbxResource, ResourceProperties, ResourceProperties_UninstallPolicy } from '@proto/integrationapp';
import { CreateIntegrationAppRequest, CreateIntegrationAppResponse_Outcome, CreatePbxIntegrationAppRequest, UpgradeIntegrationAppRequest, UpgradeIntegrationAppResponse_Outcome, UpgradePbxIntegrationAppRequest } from '@proto/odoo/integrations';
import { I18NextService } from 'angular-i18next';
import { firstValueFrom } from 'rxjs';
import { ToastService } from 'src/app/toast.service';
import { IntegrationAppsService } from '../../integration-apps.service';
import { getProvisioningTypeLabelKey, getOnUninstallLabelKey, ProvisioningEntry, LabelData, Permission, ProvisioningDialogFormType } from '../../integration-apps.types';
import { ConfirmationService } from 'primeng/api';
import { Model } from 'src/app/app.model';
import { runInAction } from 'mobx';

@Component({
  selector: 'app-integration-apps-creation-editor',
  templateUrl: './integration-apps-creation-editor.component.html',
  standalone: false
})
export class IntegrationAppsCreationEditorComponent implements OnInit {

  public integrationAppForm: FormGroup;

  // for managing the provisioning dialog
  public showDialog: boolean = false;
  public dialogContentType: ProvisioningDialogFormType = 'selectEntryType';

  // for editing an integration app
  public isInEditMode: boolean = false;
  private appId?: string;
  private existingApp?: IntegrationApp;

  public instanceProvisioningEntryList: ProvisioningEntry[] = [];
  // used to edit an entry from the table
  public editProvisioningEntry: ProvisioningEntry | undefined = undefined;

  activeAccordionPanels: string[] = ['0'];
  @ViewChild('appInformationPanel', { read: ElementRef })
  appInformationPanel!: ElementRef<HTMLElement>;

  public allPermissions: Permission[] = [
    { id: 0, label: 'contacts.read'},
    { id: 1, label: 'contacts.write'},
    { id: 2, label: 'tasks.read'},
  ];
  public availablePermissions: Permission[] = [];
  public selectedPermissions: Permission[] = [];

  // columns for the provisioning data table
  public columns = [
    { field: 'type', header: 'type_label'},
    { field: 'onUninstall', header: 'on_uninstall_label'},
    { field: 'key', header: 'key_label'}
  ];

  public apiDocsUrl = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private i18Next: I18NextService,
    private integrationService: IntegrationAppsService,
    private confirmationService: ConfirmationService,
    private m: Model
  ) {
    this.integrationAppForm = this.fb.group({
      appInformation: this.fb.group({
        name: ['', Validators.required],
        vendor: ['', Validators.required],
        documentationUrl: ['', [Validators.required, this.urlValidator()]],
        websiteUrl: ['', [Validators.required, this.urlValidator()]],
        supportEmail: ['', [Validators.required, Validators.email]],
        privacyPolicyUrl: ['', [Validators.required, this.urlValidator()]],
        description: ['', Validators.required],
        iconUrl: ['', Validators.required],
      })
    });
  }

  async ngOnInit(): Promise<void> {
    this.appId = this.route.snapshot.paramMap.get('appId') ?? undefined;
    this.isInEditMode = this.appId !== undefined;
    runInAction(() => this.apiDocsUrl = this.m.config.apiDocsUrl);

    // prepare apiPermissionList
    this.selectedPermissions = [];
    this.updateAvailablePermissions();

    // nothing more to do when not in editMode
    if (!this.isInEditMode || !this.appId) {
      return;
    }

    try {
      const response = await firstValueFrom(this.integrationService.getAppDetails(this.appId));
      if (!response.app) {
        this.toastService.error(this.i18Next.t('integration-apps-creation-editor_load_existing_app_error'), 'error');
        this.onBack();
        return;
      }
      this.existingApp = response.app;
      this.setAppInformationData(this.existingApp);
      this.setApiPermissionData(this.existingApp);
      this.setProvisioningEntryData(this.existingApp);
    } catch {
      this.toastService.error(this.i18Next.t('integration-apps-creation-editor_load_existing_app_error'), 'error');
      this.onBack();
    }
  }

  private setAppInformationData(app: IntegrationApp): void {
    this.integrationAppForm.patchValue({
      appInformation: {
        name: app.name,
        vendor: app.vendor,
        documentationUrl: this.cutHttpsPrefix(app.documentationUrl),
        websiteUrl: this.cutHttpsPrefix(app.websiteUrl),
        supportEmail: app.supportEmail,
        privacyPolicyUrl: this.cutHttpsPrefix(app.privacyPolicyUrl),
        description: app.description,
        iconUrl: app.iconUrl
      }
    });
  }

  private setApiPermissionData(app: IntegrationApp): void {
    // handle multiple permission sets per app later -> load last (latest) set?
    const scopes = app.apiPermissions?.[0]?.scopes ?? [];
    this.selectedPermissions = this.allPermissions.filter(
      permission => scopes.includes(permission.label)
    );
    this.updateAvailablePermissions();
  }

  private setProvisioningEntryData(app: IntegrationApp): void {
    // adjust if other apps than 'pbxIntegrationApps' are possible
    const resources = app.appType?.pbxIntegrationApp?.resources ?? [];

    this.instanceProvisioningEntryList = resources
      .map((resource, index): ProvisioningEntry | undefined => {
        // skip other resources than label for now
        // later, handle cases and populate correctly
        if (resource.resource?.$case !== 'label') {
          return undefined;
        }
        const label = resource.resource.label;
        const properties = resource.resourceProperties;

        const entry: LabelData = {
          id: index,
          type: 'label',
          name: label.name,
          key: properties?.integrationAppResourceKey ?? '',
          onUninstall: properties?.uninstallPolicy ===
            ResourceProperties_UninstallPolicy.UNINSTALL_POLICY_RETAIN
            ? 'keep' : 'remove',
          labelType: label.type === PbxLabel_Type.TYPE_SKILL
            ? 'skill' : 'generic',
          visible: label.visible ? 'visible' : 'invisible',
        };
        return entry;
      })
      .filter(
        (entry): entry is ProvisioningEntry => entry !== undefined
    );
  }

  private updateAvailablePermissions(): void {
    const selectedIds = new Set(
      this.selectedPermissions.map(permission => permission.id)
    );
    this.availablePermissions = this.allPermissions.filter(
      permission => !selectedIds.has(permission.id)
    );
  }

  // handles the emit from the permission-selector
  onPermissionsChange(permissions: Permission[]): void {
    this.selectedPermissions = permissions;
    this.updateAvailablePermissions();
  }

  get appInformationForm(): FormGroup {
    return this.integrationAppForm.get('appInformation') as FormGroup;
  }

  getTypeLabelKey(type: ProvisioningEntry['type']): string {
    return getProvisioningTypeLabelKey(type);
  }

  getUninstallLabelKey(value: ProvisioningEntry['onUninstall']): string {
    return getOnUninstallLabelKey(value);
  }

  onBack() {
    if (this.appId) {
      this.router.navigate(['/integration-apps/details', this.appId]);
    } else {
      this.router.navigate(['/integration-apps/hub']);
    }
  }

  private withHttpsPrefix(value: string): string {
    const trimmedValue = value.trim();
    return trimmedValue.startsWith('https://')
      ? trimmedValue
      : `https://${trimmedValue}`;
  }

  private cutHttpsPrefix(value: string): string {
    const trimmedValue = value.trim();
    return trimmedValue.startsWith('https://')
      ? trimmedValue.split('https://')[1]
      : trimmedValue;
  }

  async createIntegrationApp() {
    if (!this.checkRequiredFormFields()) {
      return;
    }

    const integrationApp = this.buildIntegrationAppProto();
    if (!integrationApp) {
      return;
    }

    const createPbxIntegrationAppRequest = CreatePbxIntegrationAppRequest.create({
      integrationApp: integrationApp,
    });
    const createReq = CreateIntegrationAppRequest.create({
      request: {
        $case: 'pbxIntegrationApp',
        pbxIntegrationApp: createPbxIntegrationAppRequest
      }
    });

    try {
      const creationResponse = await firstValueFrom(this.integrationService.createIntegrationApp(createReq));

      if (creationResponse.outcome === CreateIntegrationAppResponse_Outcome.OUTCOME_SUCCESS) {
        this.toastService.success(this.i18Next.t('integration-apps-creation-editor_create_app_success'), 'success');
        if (creationResponse.uuid) {
          this.router.navigate(['/integration-apps/details', creationResponse.uuid]);
        }
      }
      if (creationResponse.outcome === CreateIntegrationAppResponse_Outcome.OUTCOME_ERROR) {
        this.toastService.error(this.i18Next.t('integration-apps-creation-editor_create_app_error'), 'error');
      }
    } catch (error) {
      this.toastService.error(this.i18Next.t('integration-apps-creation-editor_create_app_error'), 'error');
    }
  }

  async createNewIntegrationAppVersion() {
    if (!this.checkRequiredFormFields()) {
      return;
    }

    const integrationApp = this.buildIntegrationAppProto();
    if (!integrationApp) {
      return;
    }

    const upgradePbxIntegrationAppRequest = UpgradePbxIntegrationAppRequest.create({
      integrationApp: integrationApp,
    });
    const upgradeReq = UpgradeIntegrationAppRequest.create({
      request: {
        $case: 'pbxIntegrationApp',
        pbxIntegrationApp: upgradePbxIntegrationAppRequest
      }
    });

    try {
      if (!this.existingApp?.uuid) {
        return;
      }
      const upgradeResponse = await firstValueFrom(this.integrationService.createNewIntegrationAppVersion(this.existingApp?.uuid, upgradeReq));

      if (upgradeResponse.outcome === UpgradeIntegrationAppResponse_Outcome.OUTCOME_SUCCESS) {
        this.toastService.success(this.i18Next.t('integration-apps-creation-editor_upgrade_app_success'), 'success');
        if (this.existingApp?.uuid) {
          this.router.navigate(['/integration-apps/details', this.existingApp?.uuid]);
        }
      }
      if (upgradeResponse.outcome === UpgradeIntegrationAppResponse_Outcome.OUTCOME_ERROR) {
        this.toastService.error(this.i18Next.t('integration-apps-creation-editor_upgrade_app_error'), 'error');
      }
    } catch (error) {
      this.toastService.error(this.i18Next.t('integration-apps-creation-editor_upgrade_app_error'), 'error');
    }
  }

  private buildIntegrationAppProto(): IntegrationApp {
    const raw = this.integrationAppForm.getRawValue();
    if (this.existingApp?.uuid) {
      raw.appInformation.uuid = this.existingApp?.uuid;
    }
    const appInformation = {
      ...raw.appInformation,
      documentationUrl: this.withHttpsPrefix(raw.appInformation.documentationUrl),
      websiteUrl: this.withHttpsPrefix(raw.appInformation.websiteUrl),
      privacyPolicyUrl: this.withHttpsPrefix(raw.appInformation.privacyPolicyUrl),
    };

    let resourceArray: PbxResource[] = [];
    for(let resource of this.instanceProvisioningEntryList) {
      const resourceProperties = ResourceProperties.create({
        integrationAppResourceKey: resource.key,
        uninstallPolicy: resource.onUninstall === 'keep' ?
          ResourceProperties_UninstallPolicy.UNINSTALL_POLICY_RETAIN :
          ResourceProperties_UninstallPolicy.UNINSTALL_POLICY_DELETE,
      });
      let pbxResource: PbxResource;
      if (resource.type === 'label') {
        // add other methods for other resource types
        pbxResource = this.buildLabelResourceObject(resource as LabelData, resourceProperties);
      } else {
        // skip all other than label at the moment
        continue;
      }
      resourceArray.push(pbxResource);
    }

    const pbxIntegrationApp = PbxIntegrationApp.create({
      resources: [...resourceArray]
      // ... webhooks
      // ... configuration
    });
    // handle having multiple sets later
    const scopes = this.selectedPermissions.map(permission => permission.label);
    const apiPermissions = scopes.length > 0 ? [ApiPermissions.create({
      identifier: 'defaultPermissionSet',
      scopes,
    })] : [];

    const integrationApp = IntegrationApp.create({
      ...appInformation,
      appType: {
        $case: 'pbxIntegrationApp',
        pbxIntegrationApp: pbxIntegrationApp
      },
      apiPermissions: apiPermissions,
    });

    return integrationApp;
  }

  private buildLabelResourceObject(resource: LabelData, properties: ResourceProperties): PbxResource {
    const pbxLabel = PbxLabel.create({
      name: resource.name,
      type: resource.labelType === 'generic' ? PbxLabel_Type.TYPE_GENERIC :
        PbxLabel_Type.TYPE_SKILL,
      visible: resource.visible === 'visible' ? true : false,
    });
    const pbxResource = PbxResource.create({
      resourceProperties: properties,
      resource: {
        $case: 'label',
        label: pbxLabel
      }
    });
    return pbxResource;
  }

  private checkRequiredFormFields(): boolean {
    this.integrationAppForm.markAllAsTouched();
    this.markControlsAsDirty(this.integrationAppForm);

    const invalidPanels: string[] = [];
    if (this.appInformationForm.invalid) {
      invalidPanels.push('0');
    }
    // check more panels with forms and required fields

    if (invalidPanels.length > 0) {
      this.activeAccordionPanels = [...invalidPanels];
      setTimeout(() => {
        this.appInformationPanel.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
      return false;
    }
    return true;
  }

  private markControlsAsDirty(control: AbstractControl): void {
    control.markAsDirty();
    if (control instanceof FormGroup) {
      Object.values(control.controls).forEach(childControl => {
        this.markControlsAsDirty(childControl);
      });
    }
  }

  // at the moment we require 'https'
  urlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const input = String(control.value ?? '').trim();
      if (!input){
        return null;
      }
      try {
        const url = new URL(input.startsWith('https://') ? input : `https://${input}`);
        return url.protocol === 'https:' && !!url.hostname ? null : { url: true };
      } catch {
        return { url: true };
      }
    };
  }

  onAddProvisioningEntry() {
    this.editProvisioningEntry = undefined;
    this.dialogContentType = 'fillForm';
    // use the select when more entries than label are allowed
    // this.dialogContentType = 'selectEntryType';
    this.showDialog = true;
  }

  onEditProvisioningEntry(entry: ProvisioningEntry) {
    this.editProvisioningEntry = entry;
    this.dialogContentType = 'fillForm';
    this.showDialog = true;
  }

  openDeleteConfirmDialog(id: number) {
    this.confirmationService.confirm({
      message: this.i18Next.t('integration-apps-creation-editor_remove_instance_provisioning_entry_message'),
      header: this.i18Next.t('integration-apps-creation-editor_remove_instance_provisioning_entry_header'),
      acceptLabel: this.i18Next.t('app_yes'),
      rejectLabel: this.i18Next.t('app_no'),
      accept: () => this.deleteEntryFromList(id)
    });
  }

  deleteEntryFromList(id: number) {
    this.instanceProvisioningEntryList = this.instanceProvisioningEntryList.filter(entry => entry.id !== id);
  }

  onProvisioningEntryAdded(entry: ProvisioningEntry) {

    if (entry.id === undefined || entry.id === null) {
      const existingIds = this.instanceProvisioningEntryList
        .map(item => item.id).filter((id): id is number => id != null);
      entry.id = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 0;
      this.instanceProvisioningEntryList.push(entry);
    } else {
      const entryIndex = this.instanceProvisioningEntryList.findIndex(
        existingEntry => existingEntry.id === entry.id
      );
      if (entryIndex !== -1) {
        this.instanceProvisioningEntryList[entryIndex] = entry;
      }
    }
  }

}
